import { getAuthenticationStatus } from '@seek/adv-header-footer';
import { brandAndCountryFromZone } from '@seek/audience-zones';
import {
  type Locale,
  getLanguageFromLocale,
  getZoneFromSite,
  siteConfig,
} from '@seek/melways-sites';
import type { Maybe } from 'graphql/jsutils/Maybe';

import {
  type ContractBalance,
  type PricingModel as PricingModelType,
  PricingModel,
  type PermissionOptions,
  type GetHirerAccountQuery,
  type DashboardJobsCountsFragment,
  type DashboardJobsFragment,
} from 'src/graphql/generated';
import type {
  DashboardAccountContext,
  PageData,
  PricingType,
  DashboardTalentSearchUserContext,
  EmployerSiteName,
  CorePageData,
} from 'src/types';
import { isDraftJobItem, isIndirectJob } from 'src/utils/job';

import { getHirerTypeFromContracts } from './getHirerTypeFromContracts';

const getAdvertiserTypePricing = (
  pricingModel?: Maybe<PricingModelType>,
): PricingType => {
  switch (pricingModel) {
    case PricingModel.HORIZON:
      return 'variable';
    case PricingModel.FIXED:
      return 'fixed';
  }

  return 'variable';
};

export const constructCorePageTrackingData = ({
  site,
  locale,
}: {
  site: EmployerSiteName;
  locale: Locale;
}): CorePageData => {
  const currentSite = siteConfig[site];
  const zone = site ? getZoneFromSite(site) : undefined;
  const brand = zone ? brandAndCountryFromZone(zone)?.[0] : undefined;
  return {
    currentPage: 'dashboard',
    siteSection: 'home',
    siteCountry: currentSite?.classification?.country,
    siteLanguage: getLanguageFromLocale(locale),
    brand,
    zone: site ? getZoneFromSite(site) : undefined,
  };
};

export const constructPageTrackingData = ({
  accountContext,
  tsContext,
  dashboardJobs,
  dashboardJobsCounts,
  hirerAccount,
  hirerContracts,
  site,
  locale,
}: {
  accountContext: DashboardAccountContext;
  tsContext: DashboardTalentSearchUserContext;
  dashboardJobs: DashboardJobsFragment;
  dashboardJobsCounts: DashboardJobsCountsFragment;
  hirerAccount: GetHirerAccountQuery['hirerAccount'];
  hirerContracts?: ContractBalance[];
  site: EmployerSiteName;
  locale: Locale;
}): PageData => {
  const {
    draftsCount,
    directExpired,
    directPosted,
    indirectExpired,
    indirectPosted,
  } = dashboardJobsCounts;

  const jobs = dashboardJobs.jobs.edges.map((edge) => edge.node);

  const hasPostedJobsBefore = Boolean(
    directPosted || directExpired || indirectPosted || indirectExpired,
  );

  const canCreateJobs =
    accountContext.currentAdvertiser.permissionOptions.canCreateJobs;

  const hasJobUpgradeButton =
    canCreateJobs &&
    jobs.some((job) => !isDraftJobItem(job) && job.canBeUpgraded);

  const { currentPage, siteLanguage, siteSection, brand, siteCountry, zone } =
    constructCorePageTrackingData({ site, locale });

  return {
    currentPage,
    siteSection,
    siteCountry,
    siteLanguage,
    brand,
    zone,
    hirerOriginZone: hirerAccount?.originZone,
    seekAdvertiserId: accountContext.currentAdvertiser.seekId || '',
    advertiserAdCentreId: accountContext.currentAdvertiser.advertiserId || '',
    userId: accountContext.user?.id || '',
    isLoggedIn: Boolean(accountContext.user),
    isTestAccount: hirerAccount?.isTestAccount,
    isImpersonation: Boolean(accountContext?.identityContext?.act),

    advertiserCommitmentType: getHirerTypeFromContracts(hirerContracts),
    pricingType: getAdvertiserTypePricing(hirerAccount?.pricingModel),

    isHirerRepeatAdPurchaser: hasPostedJobsBefore,
    hasPremiumTalentSearch: tsContext.hasPTSClaim,
    isActivatedEmail: hirerAccount?.activationStatus === 'ACTIVE',
    isAccountVerified: hirerAccount?.isApproved,
    isTalentSearchApproved: hirerAccount?.isTalentSearchApproved,
    accountType: tsContext?.productType?.split('_')[0].toLocaleLowerCase(),

    accountPermissions:
      accountContext.advertiser?.permissionOptions ?? ({} as PermissionOptions),
    authenticationStatus: getAuthenticationStatus(
      accountContext.identityContext,
    ),
    isAgency: Boolean(accountContext.advertiser?.isAgency),
    isSwitchAccountEnabled: Boolean(
      accountContext.advertiser?.switchAccountsHref,
    ),
    isCreditOnHold: Boolean(accountContext.advertiser?.showOnCreditHoldMessage),
    hasJobUpgradeButton,

    // These are the total numbers of jobs for this hirer (max of 20)
    adDraftCount: draftsCount ?? 0,
    adLiveCount: (directPosted ?? 0) + (indirectPosted ?? 0),
    adExpiredCount: (directExpired ?? 0) + (indirectExpired ?? 0),
    adIndirectCount: (indirectPosted ?? 0) + (indirectExpired ?? 0),
    adDirectCount: (directPosted ?? 0) + (directExpired ?? 0),

    // These numbers represent the number of jobs ads shown on the dashboard (not the totals)
    adDraftShownCount: jobs.filter((job) => isDraftJobItem(job)).length,

    adLiveShownCount: jobs.filter(
      (job) => !isDraftJobItem(job) && job.status !== 'EXPIRED',
    ).length,

    adExpiredShownCount: jobs.filter(
      (job) => !isDraftJobItem(job) && job.status === 'EXPIRED',
    ).length,

    adIndirectShownCount: jobs.filter((job) => isIndirectJob(job)).length,

    adDirectShownCount: jobs.filter(
      (job) => !isDraftJobItem(job) && job.postType === 'DIRECT',
    ).length,

    // Data points required for intercom
    hirerUserEmail: accountContext.identityContext?.email || '',
    hirerUserFullName: accountContext.currentAdvertiser.name || '',
  };
};
